import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight } from '@phosphor-icons/react';
import { LimitChangePreview } from './LimitChangePreview';
import { confirm } from './ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHORT = {
  'Sales Executive': 'SE', 'Sales Coordinator': 'SC', 'Service Executive': 'Service Exec',
  'Regional Sales Manager': 'RSM', 'National Sales Manager': 'NSM', 'Vice President': 'VP', President: 'President',
};

// basePath: '/discount-limits' (President) or '/admin/tenants/<id>/discount-limits' (Super Admin)
export const DiscountLimitsPanel = ({ basePath = '/discount-limits' }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const apply = (d) => { setData(d); setDraft(d.limits); };
  useEffect(() => {
    axios.get(`${API}${basePath}`).then((r) => apply(r.data)).catch((e) => setMsg({ type: 'err', text: e.response?.data?.detail || 'Could not load limits' }));
  }, [basePath]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const isDirty = !!data && data.editable_roles.some((r) => Number(draft[r]) !== Number(data.limits[r]));
  useEffect(() => {
    if (!isDirty) { setPreview(null); return undefined; }
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const limits = Object.fromEntries(data.editable_roles.map((r) => [r, Number(draft[r])]));
        const r = await axios.post(`${API}${basePath}/preview`, { limits });
        setPreview(r.data);
      } catch { setPreview(null); } finally { setPreviewLoading(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [draft, isDirty, data, basePath]);

  if (!data) return <div className="text-sm text-[#6D8E87]">{msg.text || 'Loading…'}</div>;

  const dirty = isDirty;
  const save = async () => {
    let fresh = preview;
    try {
      const limits = Object.fromEntries(data.editable_roles.map((r) => [r, Number(draft[r])]));
      fresh = (await axios.post(`${API}${basePath}/preview`, { limits })).data;
      setPreview(fresh);
    } catch { /* fall back to last preview; PUT still validates */ }
    if (fresh && fresh.changed >= 10) {
      const ok = await confirm({
        title: 'Confirm new discount limits',
        message: `${fresh.changed} recent quotes would route to a different approver:\n${fresh.transitions.map((t) => `• ${t.count} × ${t.label}`).join('\n')}\n\nSave the new limits?`,
        confirmText: 'Save limits',
      });
      if (!ok) return;
    }
    setBusy(true);
    setMsg({ type: '', text: '' });
    try {
      const limits = Object.fromEntries(data.editable_roles.map((r) => [r, Number(draft[r])]));
      const r = await axios.put(`${API}${basePath}`, { limits });
      apply(r.data);
      setMsg({ type: 'ok', text: 'Discount limits saved — new and edited quotes use them now.' });
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.detail || 'Could not save limits' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="discount-limits-panel">
      <p className="text-sm text-[#3B5A54]">
        Each role auto-approves discounts up to its limit. Above it, the quote goes to the lowest role whose limit covers it
        (SE <ArrowRight size={10} className="inline" /> RSM <ArrowRight size={10} className="inline" /> NSM <ArrowRight size={10} className="inline" /> VP <ArrowRight size={10} className="inline" /> President).
        Limits must rise up the ladder. Quotes already pending keep their approver.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {data.editable_roles.map((r) => (
          <label key={r} className="rounded-xl border border-[#D5E8E4] bg-white p-3 block" data-testid={`discount-limit-card-${SHORT[r].replace(/\s/g, '-').toLowerCase()}`}>
            <span className="block text-[11px] uppercase tracking-wide text-[#6D8E87] font-semibold mb-1" title={r}>{SHORT[r]}</span>
            <div className="flex items-baseline gap-1">
              <input
                type="number" min="0" max="100" step="0.5"
                value={draft[r] ?? ''}
                onChange={(e) => setDraft({ ...draft, [r]: e.target.value })}
                className="w-full text-xl font-bold font-mono text-[#052A23] bg-transparent outline-none border-b border-transparent focus:border-[#00a69c]"
                data-testid={`discount-limit-${SHORT[r].replace(/\s/g, '-').toLowerCase()}`}
              />
              <span className="text-sm text-[#6D8E87]">%</span>
            </div>
            <span className="block text-[10px] text-[#9BB5AF] mt-1">default {data.defaults[r]}%</span>
          </label>
        ))}
        <div className="rounded-xl border border-dashed border-[#D5E8E4] bg-[#F4F9F7] p-3">
          <span className="block text-[11px] uppercase tracking-wide text-[#6D8E87] font-semibold mb-1">President</span>
          <div className="text-xl font-bold font-mono text-[#052A23]">{data.floor_pct > 0 ? `${data.floor_pct}%` : '100%'}</div>
          <span className="block text-[10px] text-[#9BB5AF] mt-1">= company max</span>
        </div>
      </div>
      <LimitChangePreview preview={preview} loading={previewLoading} />
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={!dirty || busy} className="px-4 py-2 rounded-lg bg-[#00a69c] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[#008f86] transition-colors" data-testid="discount-limits-save">
          {busy ? 'Saving…' : 'Save limits'}
        </button>
        {dirty && <button onClick={() => setDraft(data.limits)} className="text-sm text-[#6D8E87] hover:underline" data-testid="discount-limits-reset">Discard</button>}
        {msg.text && <span className={`text-sm ${msg.type === 'err' ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="discount-limits-msg">{msg.text}</span>}
        {data.updated_by && !msg.text && <span className="text-xs text-[#9BB5AF]">Last changed by {data.updated_by}</span>}
      </div>
    </div>
  );
};
